import { css } from "@compiled/react";
import { resolveFieldPath } from "src/uniform/components/filterable-gallery/src/utils/filterEngine";
import { useFilterableItem } from "src/uniform/contexts/filterable-item-context";

import { Card, Image } from "@/libraries/design/components";

export interface FilterableCardProps {
    templateIdPath?: string;
    namePath?: string;
    urlSlugPath?: string;
    linkUrlPath?: string;
    oneLinerHeadlinePath?: string;
    thumbnailPath?: string;
    iconUrl?: string;
    iconUrlPath?: string;
    ctaText?: string;
    ctaTextPath?: string;
    eyebrowText?: string;
    eyebrowPath?: string;
}

const DEFAULT_PATHS = {
    templateIdPath: "sys.id",
    namePath: "fields.templateNameExternal",
    urlSlugPath: "fields.fullUrlSlug",
    oneLinerHeadlinePath: "fields.oneLinerHeadline",
    thumbnailPath: "fields.templateThumbnail.fields.file.url",
    iconUrlPath: "fields.product.0.fields.icon.fields.file.url"
};
const DEFAULT_CTA_TEXT = "Learn more";

const resolveString = (item: unknown, path: string): string | undefined => {
    const val = resolveFieldPath(item, path);
    return typeof val === "string" ? val : undefined;
};

const getTextParam = (value?: string): string | undefined =>
    value && value.trim() ? value : undefined;

const pathOrDefault = (path: string | undefined, defaultPath: string): string =>
    getTextParam(path) ?? defaultPath;

const normaliseImageUrl = (url: string): string =>
    url.startsWith("//") ? `https:${url}` : url;

const resolvePathValue = (
    item: unknown,
    path: string | undefined,
    defaultPath: string
): string | undefined => resolveString(item, pathOrDefault(path, defaultPath));

const resolveOptionalPathValue = (
    item: unknown,
    path: string | undefined
): string | undefined => {
    const resolvedPath = getTextParam(path);
    return resolvedPath ? resolveString(item, resolvedPath) : undefined;
};

const getLinkUrlPath = (props: FilterableCardProps): string =>
    getTextParam(props.linkUrlPath) ??
    getTextParam(props.urlSlugPath) ??
    DEFAULT_PATHS.urlSlugPath;

const getProductIdentity = (iconUrl?: string): JSX.Element | undefined => {
    if (!iconUrl) {
        return undefined;
    }

    return (
        <div css={productIdentityStyle} data-testid="card-product">
            <Image
                alt=""
                css={productIconStyle}
                data-testid="card-icon"
                loading="lazy"
                src={iconUrl}
            />
        </div>
    );
};

const getCtaText = (item: unknown, props: FilterableCardProps): string =>
    resolveOptionalPathValue(item, props.ctaTextPath) ??
    getTextParam(props.ctaText) ??
    DEFAULT_CTA_TEXT;

const getEyebrow = (
    item: unknown,
    props: FilterableCardProps
): string | undefined =>
    resolveOptionalPathValue(item, props.eyebrowPath) ??
    getTextParam(props.eyebrowText);

const getImage = (thumbnail?: string, name?: string) =>
    thumbnail
        ? {
              src: thumbnail,
              alt: name ?? ""
          }
        : undefined;

const getIcon = (productIdentity?: JSX.Element) =>
    productIdentity
        ? {
              iconComponent: productIdentity
          }
        : undefined;

const getCallToAction = (urlSlug?: string, name?: string, ctaText?: string) =>
    urlSlug && name && ctaText
        ? {
              href: urlSlug,
              text: ctaText,
              ariaLabel: `${ctaText} about ${name}`,
              ariaLabelledby: undefined
          }
        : undefined;

const FilterableCard = (props: FilterableCardProps): JSX.Element => {
    const item = useFilterableItem();
    const name = resolvePathValue(item, props.namePath, DEFAULT_PATHS.namePath);
    const urlSlug = resolveString(item, getLinkUrlPath(props));
    const oneLinerHeadline = resolvePathValue(
        item,
        props.oneLinerHeadlinePath,
        DEFAULT_PATHS.oneLinerHeadlinePath
    );
    const rawThumbnail = resolvePathValue(
        item,
        props.thumbnailPath,
        DEFAULT_PATHS.thumbnailPath
    );
    const rawIconUrl =
        getTextParam(props.iconUrl) ??
        resolvePathValue(item, props.iconUrlPath, DEFAULT_PATHS.iconUrlPath);
    const ctaText = getCtaText(item, props);
    const eyebrow = getEyebrow(item, props);
    const thumbnail = rawThumbnail
        ? normaliseImageUrl(rawThumbnail)
        : undefined;
    const iconUrl = rawIconUrl ? normaliseImageUrl(rawIconUrl) : undefined;
    const productIdentity = getProductIdentity(iconUrl);

    return (
        <div css={filterableCardStyle} data-testid="filterable-card">
            <Card
                title={name}
                titleAs="h2"
                eyebrow={eyebrow}
                body={oneLinerHeadline}
                image={getImage(thumbnail, name)}
                icon={getIcon(productIdentity)}
                callToAction={getCallToAction(urlSlug, name, ctaText)}
            />
        </div>
    );
};

const filterableCardStyle = css({
    "height": "100%",
    "width": "min(100%, 368px)",
    // Card does not currently expose a container style prop, so keep the sizing
    // override scoped to this Uniform wrapper.
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "> div": {
        width: "100%",
        height: "100%"
    }
});

const productIdentityStyle = css({
    display: "inline-flex",
    alignItems: "center"
});

const productIconStyle = css({
    display: "block",
    width: "32px",
    height: "32px",
    objectFit: "contain",
    flexShrink: 0
});

export default FilterableCard;
